import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import Successpopup from '../components/common/SharedComponent/Successpopup';

export interface PopupConfig {
    show: boolean;
    success: boolean;
    heading: string;
    message: string;
    showManualLink?: boolean;
    onManualLink?: () => void;
    showTryAgain?: boolean;
    onTryAgain?: () => void;
    showDepositButton?: boolean;
    onDeposit?: () => void;
}

interface PopupContextType {
    showSuccess: (
        heading: string,
        message: string,
        options?: Partial<Omit<PopupConfig, 'show' | 'success' | 'heading' | 'message'>>
    ) => void;
    showError: (
        heading: string,
        message: string,
        options?: Partial<Omit<PopupConfig, 'show' | 'success' | 'heading' | 'message'>>
    ) => void;
    closePopup: () => void;
    isOpen: boolean;
}

const defaultPopupConfig: PopupConfig = {
    show: false,
    success: true,
    heading: '',
    message: '',
    showManualLink: false,
    showTryAgain: false,
    showDepositButton: false,
};

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const PopupProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [popup, setPopup] = useState<PopupConfig>(defaultPopupConfig);

    const showSuccess = useCallback(
        (
            heading: string,
            message: string,
            options?: Partial<Omit<PopupConfig, 'show' | 'success' | 'heading' | 'message'>>
        ) => {
            setPopup((prev) => ({
                ...defaultPopupConfig,
                show: true,
                success: true,
                heading,
                message,
                ...options,
            }));
        },
        []
    );

    const showError = useCallback(
        (
            heading: string,
            message: string,
            options?: Partial<Omit<PopupConfig, 'show' | 'success' | 'heading' | 'message'>>
        ) => {
            setPopup((prev) => ({
                ...defaultPopupConfig,
                show: true,
                success: false,
                heading,
                message,
                ...options,
            }));
        },
        []
    );

    const closePopup = useCallback(() => {
        setPopup(defaultPopupConfig);
    }, []);

    const handlePopupClose = useCallback(() => {
        closePopup();
    }, [closePopup]);

    const contextValue: PopupContextType = {
        showSuccess,
        showError,
        closePopup,
        isOpen: popup.show,
    };

    return (
        <PopupContext.Provider value={contextValue}>
            {children}
            {popup.show && (
                <Successpopup
                    Heading={popup.heading}
                    Success={popup.success}
                    Message={popup.message}
                    onClose={handlePopupClose}
                    showManualLink={popup.showManualLink}
                    onManualLink={popup.onManualLink}
                    showTryAgain={popup.showTryAgain}
                    onTryAgain={popup.onTryAgain}
                    showDepositButton={popup.showDepositButton}
                    onDeposit={popup.onDeposit}
                />
            )}
        </PopupContext.Provider>
    );
};

export const usePopup = (): PopupContextType => {
    const context = useContext(PopupContext);

    if (!context) {
        throw new Error(
            '❌ usePopup must be used within a <PopupProvider>. ' +
            'Make sure your component is wrapped with PopupProvider in App.tsx'
        );
    }

    return context;
};

export { PopupContext };
